import React from "react";
import DriverDto from "../Common/dtos/DriverDto";
import { RowFormText } from "../Common/Forms/FormText";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import useUpsertDriver, { DriverUpsertDto } from "../../requests/useUpsertDriver";
import { FormSiteAssignment } from "../Common/SiteAssignment/FormSiteAssignment";
import { FormEmailList, fromEmailFields, toEmailFields } from "../Common/Forms/FormEmailList";
import useGetDriverSiteAssignment from "../../requests/useGetDriverSiteAssignment";
import useEntityForm from "../Common/useEntityForm";

interface CreateEditDriverProps {
    initialDriver: DriverDto | null;
    onDismiss: () => void;
    onUpsert: (driverId: string, driverName: string) => void;
    mode: ModalMode;
}

type DriverForm = Omit<DriverUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditDriver = (props: CreateEditDriverProps) => {
  const { id, sites: initialSites, emailAddresses: initialEmails, ...initialDriver } = { ...props.initialDriver };

  const { hookProps, control, trigger, submit, isSaving, loading, loadError, displayMessage, sites } =
    useEntityForm<DriverForm, DriverUpsertDto>({
        initialId: props.initialDriver?.id,
        defaultValues: {
            ...initialDriver,
            siteIds: [],
            emailAddresses: toEmailFields(initialEmails),
        },
        useSiteAssignment: options => useGetDriverSiteAssignment(props.initialDriver?.id, options),
        useUpsert: options => useUpsertDriver(props.initialDriver?.id, options),
        toDto: form => ({
            ...form,
            emailAddresses: fromEmailFields(form.emailAddresses),
        }),
        onSuccess: (driverId, form) => {
            props.onDismiss();
            props.onUpsert(driverId, form.name);
        },
    });

    return (<EntityModal
      mode={props.mode}
      entityName="driver"
      displayMessage={displayMessage}
      loading={loading}
      loadError={loadError}
      renderSiteAssignment={<FormSiteAssignment {...hookProps('siteIds')} control={control} sites={sites} />}
      onHide={props.onDismiss}
      isSaving={isSaving}
      onSubmit={submit}
      renderForm={(<>
          <RowFormText {...hookProps('name')}
              options={{
                  required: 'Name is required',
                  validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
              }} />
          <RowFormText {...hookProps('number')}
              options={{
                  validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
              }} />

          <RowFormText {...hookProps('password')}
              options={{
                  validate: { length: v => v.length < 51 || 'Password cannot be longer than 50 characters' }
              }} />
          <RowFormText {...hookProps("addressStreet")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressCity")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address City cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressState")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address State cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressPostalCode")}
            options={{
              validate: {
                length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters' }
            }} />
          <RowFormText {...hookProps("addressCountry")}
            options={{
              validate: {
                length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters' }
            }} />
          <RowFormText {...hookProps('phone')}
              options={{
                  validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
              }} />
          <FormEmailList {...hookProps('emailAddresses')} control={control} trigger={trigger} disabled={props.mode === ModalMode.View} max={10} />
      </>)} />);
};
